import * as React from 'react';

import i18n from 'helper/i18n';

import Checkbox from 'components/atoms/Checkbox';
import NativeMobileDevice from 'components/helper/NativeMobileDevice/NativeMobileDevice';
import { AllPlatformsComponents, NativePlatformComponent } from 'components/helper/Platform';
import Spacer from 'components/helper/Spacer';
import ElementList from 'components/molecules/ElementList';
import Rich from 'components/molecules/Rich';
import { MAIL_SETTINGS, PUSH_SETTINGS } from 'defs/profile';

import GASettingsFields from 'defs/ga/fields/settings';
import { trackSettingChange } from 'defs/ga/track/settings';
import { push as dataLayerPush } from 'helper/ga';

export type SettingsType = { [key: string]: boolean };
export type SectionType = Array<string>;
export type SectionsItemType = { Wrapper: Function; settings: SectionType };
export type SectionsType = {
	[key: string]: SectionsItemType;
};
export type DoUpdateSettingsType = (key: string, settings: SettingsType) => void;
export type OnSettingChangedType = () => void;

export type ProfileSettingsProps = {
	doUpdateSettings: DoUpdateSettingsType;
	onSettingChanged: OnSettingChangedType;
	settings: SettingsType;
	signature: string;
};

export const sections: SectionsType = {
	mail: {
		Wrapper: AllPlatformsComponents,
		settings: [MAIL_SETTINGS.INACTIVITY],
	},
	push: {
		Wrapper: NativePlatformComponent,
		settings: [PUSH_SETTINGS.INACTIVITY, PUSH_SETTINGS.SALES],
	},
};

const labelPushInfo = () => {
	const text = 'react.impulsCoach.views.Profile.notifications.section.push.info.text';
	const linkLabel = i18n('react.impulsCoach.views.Profile.notifications.section.push.info.linkLabel');
	const linkHref = i18n('react.impulsCoach.views.Profile.notifications.section.push.info.linkHref');
	return i18n(text, {
		link: `<a href="${linkHref}" target="_blank">${linkLabel}</a>`,
		__disableHtmlEncoding: true,
	});
};

const renderSection = (
	sectionKey: string,
	section: SectionType,
	settings: SettingsType,
	handleChange: OnSettingChangedType,
	update: DoUpdateSettingsType
) => {
	return (
		<React.Fragment>
			<h5 className="o-profile__title">
				{i18n(`react.impulsCoach.views.Profile.notifications.section.${sectionKey}.title`)}
			</h5>
			{sectionKey === 'push' && (
				<NativeMobileDevice
					renderNative={() => {
						return <Rich contents={labelPushInfo()} />;
					}}
				/>
			)}
			<ElementList>
				{section.map((subSection: string, index: number) => {
					return (
						<Checkbox
							name={undefined as any}
							key={`${subSection}#${index}`}
							tag="li"
							id={`${subSection}#${index}`}
							isChecked={!!settings[subSection]}
							onChange={() => {
								const newSettingValue = !settings[subSection];

								update(subSection, { [subSection]: newSettingValue });

								dataLayerPush(
									trackSettingChange({
										[GASettingsFields.Service]: subSection,
										[GASettingsFields.Status]: newSettingValue,
									} as any)
								);

								handleChange();
							}}
							label={i18n(
								`react.impulsCoach.views.Profile.notifications.section.${sectionKey}.${subSection}`
							)}
						/>
					);
				})}
			</ElementList>
		</React.Fragment>
	);
};

export default class ProfileSettings extends React.Component<ProfileSettingsProps> {
	public static displayName = 'ProfileSettings';

	public shouldComponentUpdate(nextProps: ProfileSettingsProps) {
		if (this.props.signature !== nextProps.signature) {
			return true;
		}

		return false;
	}

	public render() {
		const { settings, doUpdateSettings } = this.props;
		const sectionKeys = Object.keys(sections);

		return (
			<div className="o-profile__reminder">
				<h4 className="o-profile__reminder-title">
					{i18n('react.impulsCoach.views.Profile.notifications.title')}
				</h4>
				{sectionKeys.map((section: string, index: number) => {
					const WrapperComponent = sections[section].Wrapper;

					return (
						<WrapperComponent key={section}>
							{renderSection(
								section,
								sections[section].settings,
								settings,
								this.props.onSettingChanged,
								doUpdateSettings
							)}
							{index + 1 < sectionKeys.length && <Spacer size="m" />}
						</WrapperComponent>
					);
				})}
			</div>
		);
	}
}
